import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { AlertTriangle, CheckCircle2, CircleStop, XCircle } from 'lucide-react';
import {
  BACKGROUND_TASK_ICON_SIZE,
  BackgroundTaskPanel,
  BackgroundTaskPanelAction,
} from 'src/components/background-tasks';
import { Apollo } from 'src/gql/generated';
import { useBulkExport, useBulkExportStatus } from 'src/hooks/useBulkExport';
import { makeStyles } from 'tss-react/mui';

import { BulkExportSkippedDialog } from './BulkExportSkippedDialog';
import { AlineButton } from '../generic';

interface BulkExportTaskPanelProps {
  exportId: UUID;
  /** Remove this export's panel from the tray (dismiss / cancel / auto-dismiss). */
  onDismiss: () => void;
}

interface BulkExportPartView {
  downloadUrl: string;
  partNumber: number;
  sizeBytes: number;
}

interface BulkExportSkippedView {
  documentId: string;
  entryName: string;
  reason: string;
}

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
  detailsLink: {
    '&:hover': { textDecoration: 'underline' },
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
  },
  downloadButton: {
    alignSelf: 'center',
    borderRadius: 8,
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
    textTransform: 'none',
  },
  errorIcon: {
    color: theme.palette.error.main,
    flexShrink: 0,
    marginTop: 2,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 13,
  },
  progressBar: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: 3,
    height: 6,
  },
  progressDetail: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  rowText: {
    color: theme.palette.text.primary,
    fontSize: 13,
  },
  successIcon: {
    color: '#16a34a', // green-600
    flexShrink: 0,
  },
  warningIcon: {
    color: '#facc15', // yellow-400 (matches design's amber triangle)
    flexShrink: 0,
  },
}));

/**
 * One export's card in the shared background-task tray (bottom-right). Renders
 * a `BackgroundTaskPanel` so it stacks with uploads and other jobs instead of
 * overlaying them. The panel state lives in `useBulkExportStatus` (subscription
 * + query); this component only maps it to what the card shows.
 *
 * Three states:
 *   - PENDING / PROCESSING — the current step ("Preparing N of M documents",
 *     then "Building ZIP: N of M documents"), a Cancel action, a progress bar
 *     for that step, and its percentage as a collapsed hint.
 *   - COMPLETED — "Export ready", a row per status (ready / skipped with a
 *     "See details" link into BulkExportSkippedDialog), and one Download button
 *     per ZIP part.
 *   - FAILED — "Export failed", the error, and the skipped row when every
 *     document was dropped.
 */
export function BulkExportTaskPanel({
  exportId,
  onDismiss,
}: BulkExportTaskPanelProps): JSX.Element {
  const { classes } = useStyles();
  const { bulkExport, notFound } = useBulkExportStatus(exportId);
  const { cancelBulkExport, cancelLoading } = useBulkExport();
  const [downloadedParts, setDownloadedParts] = useState<Set<number>>(() => new Set());
  const [skippedDialogOpen, setSkippedDialogOpen] = useState(false);

  const status = bulkExport?.status ?? Apollo.BulkExportStatus.Pending;
  const documentCount = bulkExport?.documentCount ?? 0;
  const processedCount = bulkExport?.processedCount ?? 0;
  const packagedCount = bulkExport?.packagedCount ?? 0;
  const skippedCount = bulkExport?.skippedCount ?? 0;
  const skippedDocuments: BulkExportSkippedView[] = bulkExport?.skippedDocuments ?? [];
  const includedCount = Math.max(0, processedCount - skippedCount);
  const parts: BulkExportPartView[] = useMemo(() => bulkExport?.parts ?? [], [bulkExport?.parts]);
  const errorMessage = bulkExport?.errorMessage ?? null;

  const isPending = status === Apollo.BulkExportStatus.Pending;
  const isCompleted = status === Apollo.BulkExportStatus.Completed;
  const isFailed = status === Apollo.BulkExportStatus.Failed;
  const isTerminal = isCompleted || isFailed;
  const progress = getExportProgress({ documentCount, isPending, packagedCount, processedCount });

  const partsIdentity = useMemo(() => parts.map(p => p.partNumber).join(','), [parts]);
  useEffect(() => {
    if (!isCompleted) return;
    const totalParts = partsIdentity ? partsIdentity.split(',').length : 0;
    /**
     * Auto-dismiss ONLY once every part has been downloaded. We deliberately do
     * not close on a timer: there is no bulk-export history UI, so this panel is
     * the user's only handle on the finished ZIP until it expires. A timed close
     * would throw away the download the moment they looked away.
     */
    if (totalParts > 0 && downloadedParts.size >= totalParts) onDismiss();
  }, [isCompleted, onDismiss, partsIdentity, downloadedParts.size]);

  useEffect(() => {
    /**
     * A persisted id whose export no longer exists (expired after its TTL, or
     * deleted) reports NOT_FOUND from the status query; drop the stale panel
     * instead of leaving an eternal "Starting…" that has no close button. A
     * transient network/auth error is deliberately not treated as missing, so a
     * blip after refresh can't wipe a still-running export.
     */
    if (notFound) onDismiss();
  }, [notFound, onDismiss]);

  const markPartDownloaded = useCallback((partNumber: number) => {
    setDownloadedParts(prev => {
      if (prev.has(partNumber)) return prev;
      const next = new Set(prev);
      next.add(partNumber);
      return next;
    });
  }, []);

  async function handleCancel() {
    // Server-side cancel best-effort cancels the Temporal workflow and marks the
    // row FAILED ("Cancelled by user"); remove the panel on success so the user
    // doesn't see the "Export failed" flash for something they cancelled.
    if (cancelLoading) return;
    const ok = await cancelBulkExport(exportId);
    if (ok) onDismiss();
  }

  return (
    <>
      <BackgroundTaskPanel
        actions={
          !isTerminal && (
            <BackgroundTaskPanelAction
              label={cancelLoading ? 'Cancelling…' : 'Cancel export'}
              onClick={handleCancel}
            >
              <CircleStop size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />
            </BackgroundTaskPanelAction>
          )
        }
        collapsedHint={!isTerminal && progress.pct !== null ? `${progress.pct}%` : undefined}
        icon={<StatusIcon isCompleted={isCompleted} isFailed={isFailed} />}
        title={getPanelTitle({ isCompleted, isFailed, progressTitle: progress.title })}
        onClose={isTerminal ? onDismiss : undefined}
      >
        <Box className={classes.body}>
          {!isTerminal && (
            <>
              <LinearProgress
                className={classes.progressBar}
                value={progress.pct ?? 0}
                variant={progress.pct === null ? 'indeterminate' : 'determinate'}
              />
              {Boolean(progress.detail) && (
                <Typography className={classes.progressDetail}>{progress.detail}</Typography>
              )}
            </>
          )}
          {isCompleted && (
            <CompletedBody
              classes={classes}
              documentCount={documentCount}
              includedCount={includedCount}
              markPartDownloaded={markPartDownloaded}
              parts={parts}
              skippedCount={skippedCount}
              onSeeSkippedDetails={() => setSkippedDialogOpen(true)}
            />
          )}
          {isFailed && (
            <FailedBody
              classes={classes}
              errorMessage={errorMessage}
              skippedCount={skippedCount}
              onSeeSkippedDetails={() => setSkippedDialogOpen(true)}
            />
          )}
        </Box>
      </BackgroundTaskPanel>

      <BulkExportSkippedDialog
        open={skippedDialogOpen}
        skippedCount={skippedCount}
        skippedDocuments={skippedDocuments}
        onClose={() => setSkippedDialogOpen(false)}
        // onRetry intentionally omitted — Retry is wired to a future
        // BulkExportRetry mutation. The dialog renders Retry as disabled.
      />
    </>
  );
}

type StyleClasses = ReturnType<typeof useStyles>['classes'];

function getPanelTitle(args: {
  isCompleted: boolean;
  isFailed: boolean;
  progressTitle: string;
}): string {
  const { isCompleted, isFailed, progressTitle } = args;
  if (isCompleted) return 'Export ready';
  if (isFailed) return 'Export failed';
  return progressTitle;
}

interface ExportProgress {
  /** One line under the bar saying what the current step does. */
  detail: string | null;
  /** Percent through the current step, or null for an indeterminate bar. */
  pct: number | null;
  title: string;
}

/**
 * An in-flight export runs two steps on the server: every document is
 * prepared (converted or located), counted by `processedCount`, then written
 * into the ZIP, counted by `packagedCount`. Show whichever step is running
 * with its own bar, so the panel never sits at "N of N" while the ZIP builds.
 */
function getExportProgress(args: {
  documentCount: number;
  isPending: boolean;
  packagedCount: number;
  processedCount: number;
}): ExportProgress {
  const { documentCount, isPending, packagedCount, processedCount } = args;
  const total = documentCount.toLocaleString();
  const toPct = (done: number) =>
    documentCount > 0 ? Math.min(100, Math.round((done / documentCount) * 100)) : 0;

  if (isPending) return { detail: null, pct: null, title: 'Starting bulk export…' };
  if (processedCount < documentCount) {
    return {
      detail: 'Step 1 of 2: converting your documents',
      pct: toPct(processedCount),
      title: `Preparing ${processedCount.toLocaleString()} of ${total} documents`,
    };
  }
  if (packagedCount < documentCount) {
    return {
      detail: 'Step 2 of 2: adding documents to your ZIP file',
      pct: toPct(packagedCount),
      title: `Building ZIP: ${packagedCount.toLocaleString()} of ${total} documents`,
    };
  }
  return { detail: 'Uploading your ZIP file', pct: null, title: 'Finishing up…' };
}

function StatusIcon({
  isCompleted,
  isFailed,
}: {
  isCompleted: boolean;
  isFailed: boolean;
}): JSX.Element {
  if (isCompleted)
    return <CheckCircle2 color="#16a34a" size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2.25} />;
  if (isFailed)
    return <XCircle color="#dc2626" size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2.25} />;

  return <CircularProgress size={BACKGROUND_TASK_ICON_SIZE} thickness={5} />;
}

function CompletedBody(props: {
  classes: StyleClasses;
  documentCount: number;
  includedCount: number;
  markPartDownloaded: (partNumber: number) => void;
  onSeeSkippedDetails: () => void;
  parts: BulkExportPartView[];
  skippedCount: number;
}): JSX.Element {
  const {
    classes,
    documentCount,
    includedCount,
    markPartDownloaded,
    onSeeSkippedDetails,
    parts,
    skippedCount,
  } = props;
  return (
    <>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <CheckCircle2 className={classes.successIcon} size={18} strokeWidth={2.25} />
        <Typography className={classes.rowText}>
          {includedCount} of {documentCount} document
          {documentCount === 1 ? '' : 's'} ready to download
        </Typography>
      </Stack>
      {skippedCount > 0 && (
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <AlertTriangle
            className={classes.warningIcon}
            fill="#fde68a"
            size={18}
            strokeWidth={2.25}
          />
          <Typography className={classes.rowText}>
            {skippedCount} document{skippedCount === 1 ? '' : 's'} were skipped
          </Typography>
          <MuiLink className={classes.detailsLink} onClick={onSeeSkippedDetails}>
            See details
          </MuiLink>
        </Stack>
      )}
      <DownloadButtons classes={classes} markPartDownloaded={markPartDownloaded} parts={parts} />
    </>
  );
}

function FailedBody(props: {
  classes: StyleClasses;
  errorMessage: string | null;
  onSeeSkippedDetails: () => void;
  skippedCount: number;
}): JSX.Element {
  const { classes, errorMessage, onSeeSkippedDetails, skippedCount } = props;
  return (
    <>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
        <XCircle className={classes.errorIcon} size={18} strokeWidth={2.25} />
        <Typography className={classes.errorText}>
          {errorMessage ?? 'Something went wrong while exporting.'}
        </Typography>
      </Stack>
      {/**
       * The all-skipped failure (every document failed to render) persists the
       * skipped rows just like a COMPLETED export, so offer the same path into
       * the details dialog — otherwise the user sees a bare error with no way to
       * learn which documents were dropped or why.
       */}
      {skippedCount > 0 && (
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <AlertTriangle
            className={classes.warningIcon}
            fill="#fde68a"
            size={18}
            strokeWidth={2.25}
          />
          <Typography className={classes.rowText}>
            {skippedCount} document{skippedCount === 1 ? '' : 's'} were skipped
          </Typography>
          <MuiLink className={classes.detailsLink} onClick={onSeeSkippedDetails}>
            See details
          </MuiLink>
        </Stack>
      )}
    </>
  );
}

/**
 * Trigger a file download without navigating or opening a new tab.
 *
 * `target="_blank"` briefly flashes a blank tab before the browser recognizes
 * the Content-Disposition: attachment header and aborts the navigation; a plain
 * `href` replaces the current page until the same redirect kicks in. A temporary
 * `<a download>` injected and clicked is the standard way to start a same-tab
 * download from a presigned URL.
 */
function triggerDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  // The download attribute also nudges Safari/Firefox to treat the response as
  // an attachment regardless of headers. Leaving the value empty lets S3's
  // Content-Disposition supply the actual filename.
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function DownloadButtons(props: {
  classes: StyleClasses;
  markPartDownloaded: (partNumber: number) => void;
  parts: BulkExportPartView[];
}): JSX.Element | null {
  const { classes, markPartDownloaded, parts } = props;
  if (parts.length === 0) return null;

  if (parts.length === 1) {
    const part = parts[0];
    return (
      <AlineButton
        className={classes.downloadButton}
        color="primary"
        size="large"
        variant="contained"
        onClick={() => {
          markPartDownloaded(part.partNumber);
          triggerDownload(part.downloadUrl);
        }}
      >
        Download exported files
      </AlineButton>
    );
  }

  return (
    <Stack className={classes.downloadButton} spacing={0.75}>
      {parts.map(part => (
        <AlineButton
          key={part.partNumber}
          fullWidth
          color="primary"
          variant="contained"
          onClick={() => {
            markPartDownloaded(part.partNumber);
            triggerDownload(part.downloadUrl);
          }}
        >
          Download part {part.partNumber} of {parts.length} ({formatBytes(part.sizeBytes)})
        </AlineButton>
      ))}
    </Stack>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
